import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, X } from "lucide-react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import MediaRenderer from "../components/MediaRenderer";
import { db } from "../firebase";
import { DEFAULT_NEWS_ITEMS } from "../lib/newsData";
import { mergeSiteContent } from "../utils/helpers";
import { typographyFor } from "../lib/typography";

function ActionLink({ href, children, className }) {
  if (/^https?:\/\//i.test(href || "")) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  return <Link to={href || "/news"} className={className}>{children}</Link>;
}

function sortNews(list) {
  return [...list].sort((a, b) => {
    const aValue = a.date || a.createdAt?.seconds || "";
    const bValue = b.date || b.createdAt?.seconds || "";
    return String(bValue).localeCompare(String(aValue));
  });
}

function NewsModal({ item, site, onClose }) {
  useEffect(() => {
    if (!item) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [item, onClose]);

  if (!item) return null;

  const body = item.content || item.fullText || item.description || item.excerpt || "";
  const paragraphs = String(body)
    .split(/\n{2,}/)
    .map((text) => text.trim())
    .filter(Boolean);

  return (
    <div className="fixed inset-0 z-[120] bg-black/85 px-4 py-5 backdrop-blur-md md:px-6 md:py-8" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
        aria-label="Fermer l'article"
      />
      <article className="relative mx-auto flex max-h-[92vh] max-w-5xl flex-col overflow-hidden rounded-lg border border-white/10 bg-zinc-950 text-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-black/70 text-white backdrop-blur transition hover:bg-white hover:text-black"
          aria-label="Fermer"
        >
          <X size={22} />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          <div className="relative min-h-[320px] overflow-hidden bg-black md:min-h-[460px]">
            <MediaRenderer
              src={item.media || item.image}
              type={item.mediaType || "auto"}
              fallback="/images/visualizer.jpg"
              alt={item.title}
              background
              controls={item.mediaType === "video"}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-black/45 to-black/10" />
            <div className="relative flex min-h-[320px] flex-col justify-end p-6 md:min-h-[460px] md:p-10">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]">
                  {item.category || "News"}
                </span>
                {item.date && <span className="text-xs font-bold uppercase tracking-[0.18em] text-gray-300">{item.date}</span>}
              </div>
              <h2 className="mt-5 max-w-4xl text-4xl font-black uppercase leading-none md:text-6xl" style={typographyFor(site, "newsCardTitle")}>
                {item.title}
              </h2>
            </div>
          </div>

          <div className="space-y-6 p-6 md:p-10">
            {paragraphs.length ? (
              paragraphs.map((paragraph, index) => (
                <p key={`${index}-${paragraph.slice(0, 32)}`} className="readable-copy text-base text-gray-300 md:text-lg" style={typographyFor(site, "newsCardText")}>
                  {paragraph}
                </p>
              ))
            ) : (
              <p className="text-gray-500">Aucun contenu détaillé pour cette news.</p>
            )}

            {(item.externalUrl || item.href) && (
              <ActionLink
                href={item.externalUrl || item.href}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm font-bold uppercase text-white transition hover:bg-white hover:text-black"
              >
                Ouvrir le lien associé <ExternalLink size={16} />
              </ActionLink>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}

export default function News() {
  const [items, setItems] = useState([]);
  const [hasStoredItems, setHasStoredItems] = useState(false);
  const [site, setSite] = useState(() => mergeSiteContent());
  const [activeNews, setActiveNews] = useState(null);

  useEffect(() => {
    const off = onSnapshot(
      collection(db, "news"),
      (snap) => {
        setHasStoredItems(!snap.empty);
        const list = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => item.title && item.visible !== false);
        setItems(sortNews(list));
      },
      (err) => console.error("Erreur Firestore News:", err)
    );
    return () => off();
  }, []);

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      (err) => console.error("Erreur contenu News:", err)
    );
    return () => off();
  }, []);

  const hero = site.pages.home.newsHero || {};
  const page = site.pages.news || {};
  const news = useMemo(
    () => (items.length ? items : !hasStoredItems && page.useDefaultItems !== false ? DEFAULT_NEWS_ITEMS : []),
    [hasStoredItems, items, page.useDefaultItems]
  );
  const rest = news;

  return (
    <main className="min-h-screen bg-black text-white">
      <section className="px-6 pb-12 pt-24 md:px-20 md:pt-32">
        <div className="mx-auto max-w-7xl">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
            {page.eyebrow}
          </p>
          <h1 className="max-w-4xl text-5xl font-black uppercase leading-none md:text-8xl" style={typographyFor(site, "newsTitle")}>
            {page.title}
          </h1>
        </div>
      </section>

      {hero.enabled !== false && <section className="px-6 pb-10 md:px-20">
        <article className="relative mx-auto min-h-[520px] max-w-7xl overflow-hidden rounded-lg border border-white/10 bg-zinc-950">
          <MediaRenderer
            src={hero.video}
            type={hero.mediaType || "video"}
            fallback={hero.fallbackImage}
            alt={hero.title}
            background
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/25" />
          <div className="relative flex min-h-[520px] max-w-4xl flex-col justify-end p-6 md:p-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">
              {page.featuredEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-black uppercase md:text-6xl" style={typographyFor(site, "newsTitle")}>{hero.title}</h2>
            {hero.subtitle && <p className="readable-copy mt-4 max-w-2xl text-gray-200" style={typographyFor(site, "newsCardText")}>{hero.subtitle}</p>}
            <div className="mt-7 flex flex-wrap gap-3">
              <ActionLink
                href={hero.watchUrl}
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white"
              >
                {hero.watchLabel || "Regarder"} <ArrowUpRight size={16} />
              </ActionLink>
              <Link
                to="/news"
                className="rounded-full border border-white/25 px-5 py-3 text-sm font-bold uppercase transition hover:bg-white hover:text-black"
              >
                {page.allNewsLabel}
              </Link>
            </div>
          </div>
        </article>
      </section>}

      <section className="px-6 pb-20 pt-8 md:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">{page.journalEyebrow}</p>
            <h2 className="mt-3 text-3xl font-black uppercase md:text-5xl" style={typographyFor(site, "newsTitle")}>{page.journalTitle}</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((item, index) => (
              <article key={item.id || `${item.title}-${index}`} className="group overflow-hidden rounded-lg border border-white/10 bg-zinc-950 transition hover:-translate-y-1 hover:border-red-500/60">
                <button type="button" onClick={() => setActiveNews(item)} className="block w-full text-left">
                <div className="aspect-[16/10] overflow-hidden bg-zinc-900">
                  <MediaRenderer
                    src={item.media || item.image}
                    type={item.mediaType || "auto"}
                    fallback="/images/visualizer.jpg"
                    alt={item.title}
                    background
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                </div>
                </button>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">
                      {item.category || "News"}
                    </p>
                    {item.date && <p className="text-xs text-gray-500">{item.date}</p>}
                  </div>
                  <button type="button" onClick={() => setActiveNews(item)} className="mt-3 block text-left">
                    <h3 className="text-2xl font-bold leading-tight transition group-hover:text-red-100" style={typographyFor(site, "newsCardTitle")}>{item.title}</h3>
                  </button>
                  <p className="readable-copy mt-3 line-clamp-4 text-sm text-gray-400" style={typographyFor(site, "newsCardText")}>
                    {item.description || item.excerpt}
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveNews(item)}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white transition hover:text-red-500"
                  >
                    {item.buttonLabel || "Lire la news"} <ArrowUpRight size={15} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <NewsModal item={activeNews} site={site} onClose={() => setActiveNews(null)} />
    </main>
  );
}
